import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Calculator, Car, CheckCircle2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { getPrijslijst, type Prijslijst } from "../lib/prijslijst.functions";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

type PriceOption = {
  label: string;
  unit: string;
  low: number;
  high: number;
  materialShare: number;
};

type PriceConfig = {
  options: PriceOption[];
  defaultQuantity: number;
  maxQuantity: number;
};

const DEFAULT_CONFIG: PriceConfig = {
  options: [
    { label: "Stopcontact of schakelaar", unit: "punt", low: 90, high: 180, materialShare: 0.35 },
    { label: "Nieuwe elektragroep met leiding", unit: "groep", low: 250, high: 520, materialShare: 0.4 },
    { label: "Ruimte opnieuw bedraden", unit: "ruimte", low: 650, high: 1400, materialShare: 0.45 },
  ],
  defaultQuantity: 2,
  maxQuantity: 20,
};

const PRICE_CONFIGS: Record<string, PriceConfig> = {
  "storing-verhelpen": {
    options: [
      { label: "Storing opsporen en herstellen", unit: "bezoek", low: 95, high: 220, materialShare: 0.15 },
      { label: "Defect stopcontact of schakelaar", unit: "punt", low: 85, high: 165, materialShare: 0.3 },
      { label: "Spoedstoring buiten werktijd", unit: "bezoek", low: 175, high: 350, materialShare: 0.1 },
    ],
    defaultQuantity: 1,
    maxQuantity: 5,
  },
  "krachtstroom-aanleggen": {
    options: [
      { label: "Krachtstroomgroep en korte kabel", unit: "aansluiting", low: 350, high: 600, materialShare: 0.4 },
      { label: "Krachtstroom naar garage of schuur", unit: "aansluiting", low: 550, high: 950, materialShare: 0.45 },
      { label: "Extra CEE contactdoos", unit: "stuk", low: 95, high: 210, materialShare: 0.4 },
    ],
    defaultQuantity: 1,
    maxQuantity: 4,
  },
  "perilex-aansluiten": {
    options: [
      { label: "Perilex contactdoos op bestaande groep", unit: "aansluiting", low: 150, high: 260, materialShare: 0.3 },
      { label: "Perilex met nieuwe kabel en groep", unit: "aansluiting", low: 260, high: 450, materialShare: 0.4 },
      { label: "Fornuis of kookplaat aansluiten", unit: "apparaat", low: 65, high: 140, materialShare: 0.15 },
    ],
    defaultQuantity: 1,
    maxQuantity: 3,
  },
  "elektra-aanleggen": DEFAULT_CONFIG,
  groepenkast: {
    options: [
      { label: "Groepenkast vervangen", unit: "kast", low: 750, high: 1400, materialShare: 0.55 },
      { label: "Extra groep plaatsen", unit: "groep", low: 140, high: 280, materialShare: 0.45 },
      { label: "3-fase groepenkast plaatsen", unit: "kast", low: 1000, high: 1850, materialShare: 0.55 },
    ],
    defaultQuantity: 1,
    maxQuantity: 6,
  },
  databekabeling: {
    options: [
      { label: "Netwerkaansluiting aanleggen", unit: "punt", low: 110, high: 220, materialShare: 0.35 },
      { label: "Accesspoint plaatsen", unit: "punt", low: 180, high: 390, materialShare: 0.5 },
      { label: "Klein netwerk met patchkast", unit: "installatie", low: 600, high: 1350, materialShare: 0.5 },
    ],
    defaultQuantity: 2,
    maxQuantity: 16,
  },
  "laadpaal-installeren": {
    options: [
      { label: "Wallbox aan de gevel", unit: "installatie", low: 950, high: 1650, materialShare: 0.6 },
      { label: "Vrijstaande laadpaal", unit: "installatie", low: 1250, high: 2200, materialShare: 0.6 },
      { label: "Load balancing toevoegen", unit: "installatie", low: 250, high: 550, materialShare: 0.55 },
    ],
    defaultQuantity: 1,
    maxQuantity: 3,
  },
  "verlichting-aanleggen": {
    options: [
      { label: "Inbouwspot plaatsen", unit: "spot", low: 55, high: 110, materialShare: 0.4 },
      { label: "Lichtpunt met schakelaar", unit: "punt", low: 120, high: 260, materialShare: 0.35 },
      { label: "Buitenverlichting aanleggen", unit: "punt", low: 150, high: 340, materialShare: 0.45 },
    ],
    defaultQuantity: 4,
    maxQuantity: 24,
  },
  camerabeveiliging: {
    options: [
      { label: "Buitencamera plaatsen", unit: "camera", low: 250, high: 550, materialShare: 0.5 },
      { label: "Binnencamera plaatsen", unit: "camera", low: 150, high: 350, materialShare: 0.5 },
      { label: "Complete set met recorder", unit: "installatie", low: 850, high: 2400, materialShare: 0.55 },
    ],
    defaultQuantity: 2,
    maxQuantity: 8,
  },
  inbraakbeveiliging: {
    options: [
      { label: "Draadloos alarmsysteem", unit: "installatie", low: 700, high: 1900, materialShare: 0.5 },
      { label: "Bedraad alarmsysteem", unit: "installatie", low: 900, high: 2600, materialShare: 0.5 },
      { label: "Extra sensor of detector", unit: "stuk", low: 60, high: 160, materialShare: 0.55 },
    ],
    defaultQuantity: 1,
    maxQuantity: 3,
  },
  "intercom-videodeurbel": {
    options: [
      { label: "Videodeurbel installeren", unit: "installatie", low: 150, high: 650, materialShare: 0.55 },
      { label: "Video-intercom voor woning", unit: "installatie", low: 450, high: 1500, materialShare: 0.55 },
      { label: "Beltrafo of bekabeling vernieuwen", unit: "klus", low: 85, high: 220, materialShare: 0.35 },
    ],
    defaultQuantity: 1,
    maxQuantity: 2,
  },
};

const COMPLEXITY = {
  eenvoudig: { label: "Goed bereikbaar", factor: 0.9 },
  standaard: { label: "Normale situatie", factor: 1 },
  lastig: { label: "Lastig bereikbaar / oud leidingwerk", factor: 1.3 },
} as const;

type ComplexityKey = keyof typeof COMPLEXITY;

function euro(value: number) {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Math.round(value / 5) * 5);
}

export function PriceCalculator({
  dienstSlug,
  dienstNaam,
  plaatsNaam,
  plaatsSlug,
}: {
  dienstSlug: string;
  dienstNaam: string;
  plaatsNaam?: string;
  plaatsSlug?: string;
}) {
  const fallback = PRICE_CONFIGS[dienstSlug] ?? DEFAULT_CONFIG;
  const laadPrijslijst = useServerFn(getPrijslijst);
  const [lijst, setLijst] = useState<Prijslijst | null>(null);
  const [optionIndex, setOptionIndex] = useState("0");
  const [quantity, setQuantity] = useState(fallback.defaultQuantity);
  const [complexity, setComplexity] = useState<ComplexityKey>("standaard");
  const [distance, setDistance] = useState(plaatsNaam ? 10 : 25);

  useEffect(() => {
    let actief = true;
    laadPrijslijst({ data: { dienstSlug, plaatsSlug } })
      .then((resultaat) => {
        if (!actief) return;
        if (resultaat.opties.length > 0) {
          setLijst(resultaat);
          setOptionIndex("0");
          setQuantity(resultaat.opties[0]!.standaardAantal);
        }
        if (resultaat.afstandKm != null) setDistance(resultaat.afstandKm);
      })
      .catch(() => undefined);
    return () => {
      actief = false;
    };
  }, [dienstSlug, laadPrijslijst, plaatsSlug]);

  const config: PriceConfig = useMemo(() => {
    if (!lijst || lijst.opties.length === 0) return fallback;
    const eerste = lijst.opties[0]!;
    return {
      options: lijst.opties.map((o) => ({
        label: o.label,
        unit: o.eenheid,
        low: o.laag,
        high: o.hoog,
        materialShare: o.materiaalAandeel,
      })),
      defaultQuantity: eerste.standaardAantal,
      maxQuantity: Math.max(...lijst.opties.map((o) => o.maxAantal)),
    };
  }, [fallback, lijst]);

  const estimate = useMemo(() => {
    const option = config.options[Number(optionIndex)] ?? config.options[0] ?? DEFAULT_CONFIG.options[0];
    if (!option) throw new Error("Geen prijsoptie beschikbaar");
    const factor = COMPLEXITY[complexity].factor;
    const travel = Math.max(0, distance - 15) * 0.7;
    const low = option.low * quantity * factor + travel;
    const high = option.high * quantity * factor + travel;
    const materialLow = low * option.materialShare;
    const materialHigh = high * option.materialShare;
    return {
      option,
      low,
      high,
      travel,
      labourLow: low - materialLow - travel,
      labourHigh: high - materialHigh - travel,
      materialLow,
      materialHigh,
    };
  }, [complexity, config.options, distance, optionIndex, quantity]);

  return (
    <section className="mt-10 overflow-hidden rounded-lg border border-border bg-card" aria-labelledby={`calculator-${dienstSlug}`}>
      <div className="border-b border-border bg-secondary/60 px-5 py-5 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Calculator aria-hidden="true" className="size-5" />
          </span>
          <div>
            <h2 id={`calculator-${dienstSlug}`} className="text-xl font-bold">Bereken je prijsindicatie</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Voor {dienstNaam.toLowerCase()}{plaatsNaam ? ` in ${plaatsNaam}` : " in jouw regio"}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-[minmax(0,1fr)_minmax(240px,0.8fr)]">
        <div className="space-y-5">
          <label className="block space-y-2 text-sm font-semibold">
            Welke klus wil je laten uitvoeren?
            <Select value={optionIndex} onValueChange={(value) => {
              setOptionIndex(value);
              setQuantity(config.defaultQuantity);
            }}>
              <SelectTrigger className="h-11 bg-background font-normal" aria-label="Kies de klus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {config.options.map((option, index) => (
                  <SelectItem key={option.label} value={String(index)}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="block space-y-2 text-sm font-semibold">
            Aantal {estimate.option.unit === "installatie" ? "installaties" : `${estimate.option.unit}${quantity === 1 ? "" : "en"}`}
            <Input
              type="number"
              min={1}
              max={config.maxQuantity}
              value={quantity}
              onChange={(event) => setQuantity(Math.min(config.maxQuantity, Math.max(1, Number(event.target.value) || 1)))}
              className="h-11 bg-background"
            />
          </label>

          <label className="block space-y-2 text-sm font-semibold">
            Situatie ter plaatse
            <Select value={complexity} onValueChange={(value) => setComplexity(value as ComplexityKey)}>
              <SelectTrigger className="h-11 bg-background font-normal" aria-label="Kies de bereikbaarheid">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(COMPLEXITY).map(([key, value]) => (
                  <SelectItem key={key} value={key}>{value.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="block space-y-2 text-sm font-semibold">
            <span className="flex items-center gap-2">
              <Car aria-hidden="true" className="size-4 text-primary" />
              Geschatte enkele reis naar de klus
            </span>
            <span className="flex items-center gap-3">
              <Input
                type="number"
                min={0}
                max={250}
                value={distance}
                onChange={(event) => setDistance(Math.min(250, Math.max(0, Number(event.target.value) || 0)))}
                className="h-11 bg-background"
              />
              <span className="shrink-0 font-normal text-muted-foreground">km</span>
            </span>
          </label>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => {
              setOptionIndex("0");
              setQuantity(config.defaultQuantity);
              setComplexity("standaard");
              setDistance(plaatsNaam ? 10 : 25);
            }}
          >
            Vul voorbeeldklus in
          </Button>
        </div>

        <div className="flex flex-col rounded-lg bg-primary p-5 text-primary-foreground">
          <p className="text-sm font-semibold opacity-90">Geschatte totaalprijs</p>
          <p className="mt-2 text-3xl font-bold" aria-live="polite">
            {euro(estimate.low)} tot {euro(estimate.high)}
          </p>
          <p className="mt-1 text-xs opacity-80">Inclusief btw, arbeid en gebruikelijke materialen</p>

          <dl className="mt-6 space-y-3 border-t border-primary-foreground/25 pt-4 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="opacity-85">Arbeid</dt>
              <dd className="font-semibold">{euro(estimate.labourLow)} tot {euro(estimate.labourHigh)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="opacity-85">Materiaal</dt>
              <dd className="font-semibold">{euro(estimate.materialLow)} tot {euro(estimate.materialHigh)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="opacity-85">Afstandstoeslag</dt>
              <dd className="font-semibold">{estimate.travel > 0 ? euro(estimate.travel) : "Inbegrepen"}</dd>
            </div>
          </dl>

          <div className="mt-auto pt-6">
            <Button asChild variant="secondary" size="lg" className="w-full text-secondary-foreground">
              <Link to="/aanvraag">Vergelijk gratis offertes</Link>
            </Button>
            <p className="mt-3 flex gap-2 text-xs leading-relaxed opacity-85">
              <CheckCircle2 aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              Dit is een marktindicatie, geen bindende prijs. De vakman bepaalt de definitieve prijs na beoordeling.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
